<template>
  <div data-testid="form-radio">
    <div
      class="flex"
      :class="radioLabelContainerCustomCssClasses">
      <input
        data-testid="radio-input"
        type="radio"
        :name="name"
        :id="inputId"
        :value="value"
        v-model="inputBoundValue"
        :required="isRequired"
        :disabled="isDisabled"
        :aria-invalid="isInvalid"
        :aria-errormessage="errorMessageId"
        :aria-describedby="guidanceMessageId"
        class="peer absolute z-10 ms-[.44em] mt-[.44em] aspect-square h-[1.5em] cursor-pointer appearance-none opacity-0 disabled:cursor-not-allowed"
        :class="radioCustomCssClasses"
        @change="$emit('custom-change', $event)"
        @focus="$emit('custom-focus', $event)"
        @blur="$emit('custom-blur', $event)"
      />
      <span
        aria-hidden="true"
        class="absolute ms-[.44em] mt-[.44em] inline-flex aspect-square h-[1.5em] shrink-0 items-center justify-center rounded-full border-2 border-dark-grey bg-white text-royal-blue-100 ring-2 ring-inset ring-white ring-offset-[.056em] peer-checked:border-current peer-checked:bg-current peer-hover:border-current peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-royal-blue-100 peer-disabled:text-royal-blue-20"
        :class="[
          ...invalidStateFauxRadioCssClasses,
          ...fauxRadioCustomCssClasses,
        ]"
      >
      </span>
      <slot name="labelTextLong">
        <label
          data-testid="radio-label"
          :for="inputId"
          class="flex-auto cursor-pointer py-[.44em] pe-[.44em] ps-[2.39em] text-royal-blue-160 hover:bg-science-blue-5 peer-disabled:cursor-not-allowed peer-disabled:text-royal-blue-20"
          :class="labelCustomCssClasses"
        >{{ labelText }}</label
        >
      </slot>
    </div>
    <div
      v-if="showGuidanceMessage"
      :id="guidanceMessageId"
      data-testid="radio-guidance"
    >
      <slot name="guidanceMessageLong">
        <p class="mb-[.5em] ps-[2.5em]">
          {{ guidanceMessage }}
        </p>
      </slot>
    </div>

    <div
      v-if="showErrorMessage"
      role="alert"
      :id="errorMessageId"
      data-testid="radio-error-message"
    >
      <slot name="errorMessageLong">
        <div class="mb-[.5em] flex gap-x-[.5em] ps-[.5em]">
          <WarningTriangleIcon
            title="Error:"
            class="shrink-0 text-ruby-red-100"
            width="1.5em"
            height="1.5em"
          />
          <p>
            {{ errorMessage }}
          </p>
        </div>
      </slot>
    </div>
  </div>
</template>

<script>
import WarningTriangleIcon from './icons/WarningTriangleIcon.vue'

/** Radios allow users to select a single option out of various available options (unlike checkboxes that allow for multiple selections). */
export default {
  name: 'FormRadio',
  props: {
    /** The id for the input, and used to asociate with the input's label. */
    inputId: {
      type: String,
      required: true,
    },
    /** Required for accessibility purposes, to associate multiple FormRadios. Without this, screen readers cannot identify whether multiple radios are related.  */
    name: {
      type: String,
      required: true,
    },
    /** Value that the radio assigns when selected/checked. */
    value: {
      type: [String, Number, Boolean],
      required: true,
    },
    /** The bound variable that is assigned the value of the checked radio. */
    modelValue: {
      type: [String, Number, Boolean],
      default: null,
    },
    /** Describes what the user is choosing when they select the radio. */
    labelText: {
      type: String,
      required: true,
    },
    /** For accessibility (and validation if HTML5 validation api were to be used, but likely will be managed in Vue instead). Indicates whether the radio must be selected, or any radio sharing a common name prop value. */
    isRequired: {
      type: Boolean,
      default: false,
    },
    /** Disables the user from editing/focusing the input. */
    isDisabled: {
      type: Boolean,
      default: false,
    },
    /** For accessibility, to communicate to screen readers that the input is currently invalid. Also used to apply css classes. */
    isInvalid: {
      type: Boolean,
      default: false,
    },
    /** For usability, to provide additional guidance to the user about the radio. For example if a radio is disabled, explain why */
    guidanceMessage: {
      type: String,
      default: '',
    },
    /** Error message shown to the user, explaining the problem and how they can correct it. Notes: this is configured to be shown when isInvalid property is assigned as true. Can be replaced using corresponding slot. Typically, when this component is used in a fieldset, this prop will not be needed. Instead it is recommended to use the common errorMessage prop of the fieldset. Included here for edge cases where a fieldset may not be used and an error message needs to be displayed adjacent to each radio. */
    errorMessage: {
      type: String,
      default: '',
    },
    /** Tailwind CSS classes can be passed to parent container of radio and label for edge-case additional styling requirements. Considered an escape-hatch approach. */
    radioLabelContainerCustomCssClasses: {
      type: Array,
      default: () => [],
    },
    /** Tailwind CSS classes can be passed to faux radio for edge-case additional styling requirements. Considered an escape-hatch approach. */
    radioCustomCssClasses: {
      type: Array,
      default: () => [],
    },
    /** Tailwind CSS classes can be passed to faux radio for edge-case additional styling requirements. Considered an escape-hatch approach. */
    fauxRadioCustomCssClasses: {
      type: Array,
      default: () => [],
    },
    /** Tailwind CSS classes can be passed to label for edge-case additional styling requirements. Considered an escape-hatch approach. Use equivalent slot for full control.  */
    labelCustomCssClasses: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    inputBoundValue: {
      // v-model binding for the input. Using getter and setters has the advantage of 1) managing the input's checked state, which would otherwise neeed a checked attribute and computed property to manage the checked state, and 2) negates the need for additional @change handler/method to emit/update the modelValue. This approach is more concise and easier to read.
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      },
    },
    showErrorMessage() {
      // for accessibility, only when the radio is invalid and an error message prop or slot has been provided
      return (
        this.isInvalid && (this.errorMessage || !!this.$slots.errorMessageLong)
      )
    },
    errorMessageId() {
      // for accessibility, generate an id for the error message, if there is one, to associate error message with the invalid input. Otherwise return undefined for vue to remove the attribute.
      return this.showErrorMessage ? `error-${this.inputId}` : undefined
    },
    invalidStateFauxRadioCssClasses() {
      // apply css classes to the input when it is invalid
      // Question: Why not use css :invalid pseudo selector/classes instead? Answer: Because for radios with required attribute, :invalid styles will be applied when the it is unchecked and before the user has interacted, which is not the desired behaviour for UX.
      return this.isInvalid ? ['border-ruby-red-100'] : []
    },
    showGuidanceMessage() {
      return this.guidanceMessage || !!this.$slots.guidanceMessageLong
    },
    guidanceMessageId() {
      // for accessibility, generate an id for the error message, if there is one, to associate error message with the input. Otherwise return undefined for vue to remove the attribute.
      return this.showGuidanceMessage ? `guidance-${this.inputId}` : undefined
    },
  },
  emits: ['update:modelValue', 'custom-change', 'custom-focus', 'custom-blur'], // custom-* events available for potential input validation/error message handling
  components: {
    WarningTriangleIcon,
  },
}
</script>
